import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Table, Badge, Button, Modal, Form, Dropdown, InputGroup } from 'react-bootstrap';
import { battlePersonApi } from '../../api/battlePerson';
import { battlesApi } from '../../api/battles';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminBattleParticipants.css';

const AdminBattleParticipants = () => {
    const { battleId: urlBattleId } = useParams();
    const navigate = useNavigate();

    const [participants, setParticipants] = useState([]);
    const [battles, setBattles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Фильтры
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [battleFilter, setBattleFilter] = useState(urlBattleId || 'ALL');

    // Модальное окно деталей
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);

    // Загрузка участников
    const loadParticipants = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {};
            if (statusFilter !== 'ALL') {
                params.status = statusFilter;
            }
            if (roleFilter !== 'ALL') {
                params.role = roleFilter;
            }
            if (battleFilter !== 'ALL') {
                params.battleId = battleFilter;
            }

            const data = await battlePersonApi.list(params);
            console.log('[AdminBattleParticipants] Loaded participants:', data);

            let participantsList = [];
            let total = 0;

            if (Array.isArray(data)) {
                participantsList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                participantsList = data.content;
                total = data.totalElements || data.total || data.content.length;
            }

            setParticipants(participantsList);
            setTotalItems(total);
        } catch (err) {
            setError(`Не удалось загрузить участников: ${err.message}`);
            console.error('[AdminBattleParticipants] Failed to load participants:', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, roleFilter, battleFilter]);

    // Загрузка списка баттлов для фильтра
    const loadBattles = useCallback(async () => {
        try {
            const data = await battlesApi.list({ size: 200 });
            let battlesList = [];
            if (Array.isArray(data)) {
                battlesList = data;
            } else if (data && Array.isArray(data.content)) {
                battlesList = data.content;
            }
            setBattles(battlesList);
        } catch (err) {
            console.warn('[AdminBattleParticipants] Failed to load battles for filter:', err.message);
        }
    }, []);

    useEffect(() => {
        loadParticipants();
    }, [loadParticipants, currentPage, pageSize]);

    useEffect(() => {
        loadBattles();
    }, [loadBattles]);

    // Синхронизация фильтра с URL-параметром
    useEffect(() => {
        if (urlBattleId) {
            setBattleFilter(urlBattleId);
        }
    }, [urlBattleId]);

    // Фильтрация по поиску
    const filteredParticipants = participants.filter(participant =>
        participant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        participant.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        participant.city?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Получение цвета бейджа статуса
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'APPROVED':
                return 'success';
            case 'REJECTED':
                return 'danger';
            case 'WITHDRAWN':
                return 'secondary';
            case 'PENDING':
            default:
                return 'warning';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PENDING':
                return 'Ожидает';
            case 'APPROVED':
                return 'Одобрена';
            case 'REJECTED':
                return 'Отклонена';
            case 'WITHDRAWN':
                return 'Взята назад';
            default:
                return status;
        }
    };

    // Получение цвета бейджа роли
    const getRoleBadgeVariant = (role) => {
        switch (role) {
            case 'JUDGE':
                return 'primary';
            case 'PARTICIPANT':
                return 'info';
            case 'ORGANIZER':
                return 'success';
            default:
                return 'secondary';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'PARTICIPANT':
                return 'Участник';
            case 'JUDGE':
                return 'Жюри';
            case 'ORGANIZER':
                return 'Организатор';
            default:
                return role;
        }
    };

    // Одобрить заявку
    const handleApprove = async (participant) => {
        if (!window.confirm(`Одобрить заявку "${participant.name}" на баттл "${participant.battle?.title || participant.battleId}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await battlePersonApi.approve(participant.id);
            await loadParticipants();
        } catch (err) {
            setError(`Не удалось одобрить заявку: ${err.message}`);
            console.error('[AdminBattleParticipants] Failed to approve:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Отклонить заявку
    const handleReject = async (participant) => {
        if (!window.confirm(`Отклонить заявку "${participant.name}" на баттл "${participant.battle?.title || participant.battleId}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await battlePersonApi.reject(participant.id);
            await loadParticipants();
        } catch (err) {
            setError(`Не удалось отклонить заявку: ${err.message}`);
            console.error('[AdminBattleParticipants] Failed to reject:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Открытие модального окна с деталями
    const handleViewDetails = (participant) => {
        setSelectedParticipant(participant);
        setShowDetailModal(true);
    };

    // Получение названия баттла
    const getBattleTitle = (participant) => {
        if (participant.battle?.title) {
            return participant.battle.title;
        }
        const battle = battles.find(b => b.id === participant.battleId);
        return battle?.title || `Баттл #${participant.battleId}`;
    };

    const currentBattleTitle = urlBattleId
        ? battles.find(b => b.id === urlBattleId)?.title || `Баттл #${urlBattleId}`
        : null;

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">
                {currentBattleTitle
                    ? `Заявки участников — ${currentBattleTitle}`
                    : 'Модерация заявок на баттлы'}
            </h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-filters-row">
                <div className="admin-search-bar">
                    <InputGroup>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Поиск по имени, никнейму, городу..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                </div>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="status-filter">
                        Статус: {statusFilter === 'ALL' ? 'Все' : getStatusLabel(statusFilter)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setStatusFilter('ALL')}>Все</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('PENDING')}>Ожидают</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('APPROVED')}>Одобрены</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('REJECTED')}>Отклонены</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('WITHDRAWN')}>Взяты назад</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="role-filter">
                        Роль: {roleFilter === 'ALL' ? 'Все' : getRoleLabel(roleFilter)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setRoleFilter('ALL')}>Все</Dropdown.Item>
                        <Dropdown.Item onClick={() => setRoleFilter('ORGANIZER')}>Организатор</Dropdown.Item>
                        <Dropdown.Item onClick={() => setRoleFilter('PARTICIPANT')}>Участник</Dropdown.Item>
                        <Dropdown.Item onClick={() => setRoleFilter('JUDGE')}>Жюри</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                {!urlBattleId && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" id="battle-filter">
                            Баттл: {battleFilter === 'ALL' ? 'Все' : battles.find(b => b.id === battleFilter)?.title || 'Все'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <Dropdown.Item onClick={() => setBattleFilter('ALL')}>Все</Dropdown.Item>
                            {battles.map(battle => (
                                <Dropdown.Item
                                    key={battle.id}
                                    onClick={() => setBattleFilter(battle.id)}
                                >
                                    {battle.title}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                {urlBattleId && (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate('/admin/battles/participants')}
                    >
                        Показать все заявки
                    </Button>
                )}
            </div>

            <HrnbCard>
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-participants-table">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Имя</th>
                        <th>Никнейм</th>
                        <th>Город</th>
                        {!urlBattleId && <th>Баттл</th>}
                        <th>Роль</th>
                        <th>Статус</th>
                        <th>Дата подачи</th>
                        <th>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredParticipants.length === 0 ? (
                        <tr>
                            <td colSpan={urlBattleId ? 8 : 9} className="text-center" style={{ color: '#b0b6c3' }}>
                                {searchQuery ? 'Заявки не найдены' : 'Заявок пока нет'}
                            </td>
                        </tr>
                    ) : (
                        filteredParticipants.map((participant) => (
                            <tr key={participant.id}>
                                <td>{participant.id}</td>
                                <td>
                                    <strong>{participant.name}</strong>
                                </td>
                                <td style={{ color: '#b0b6c3' }}>
                                    {participant.nickname || '-'}
                                </td>
                                <td>{participant.city || '-'}</td>
                                {!urlBattleId && (
                                    <td>
                                            <span className="battle-title">
                                                {getBattleTitle(participant)}
                                            </span>
                                    </td>
                                )}
                                <td>
                                    <Badge bg={getRoleBadgeVariant(participant.role)}>
                                        {getRoleLabel(participant.role)}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge bg={getStatusBadgeVariant(participant.applicationStatus)}>
                                        {getStatusLabel(participant.applicationStatus)}
                                    </Badge>
                                </td>
                                <td>
                                    {participant.appliedAt
                                        ? new Date(participant.appliedAt).toLocaleDateString('ru-RU')
                                        : '-'}
                                </td>
                                <td>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-1"
                                        onClick={() => handleViewDetails(participant)}
                                    >
                                        👁️
                                    </Button>

                                    {participant.applicationStatus === 'PENDING' && (
                                        <>
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleApprove(participant)}
                                                disabled={actionLoading}
                                            >
                                                ✓
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleReject(participant)}
                                                disabled={actionLoading}
                                            >
                                                ✗
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно с деталями заявки */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Детали заявки</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedParticipant && (
                        <>
                            <div className="participant-header mb-4">
                                <h5>{selectedParticipant.name}</h5>
                                <div style={{ color: '#b0b6c3', fontSize: '0.875rem' }}>
                                    Никнейм: <strong>{selectedParticipant.nickname || '-'}</strong> |
                                    Город: <strong>{selectedParticipant.city || '-'}</strong>
                                </div>
                            </div>

                            <hr />

                            <div className="participant-details">
                                <h6>Информация о заявке</h6>
                                <table className="table table-sm">
                                    <tbody>
                                    <tr>
                                        <td><strong>ID заявки:</strong></td>
                                        <td>{selectedParticipant.id}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Баттл:</strong></td>
                                        <td>{getBattleTitle(selectedParticipant)}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Роль:</strong></td>
                                        <td>
                                            <Badge bg={getRoleBadgeVariant(selectedParticipant.role)}>
                                                {getRoleLabel(selectedParticipant.role)}
                                            </Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>Статус:</strong></td>
                                        <td>
                                            <Badge bg={getStatusBadgeVariant(selectedParticipant.applicationStatus)}>
                                                {getStatusLabel(selectedParticipant.applicationStatus)}
                                            </Badge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>Дата подачи:</strong></td>
                                        <td>
                                            {selectedParticipant.appliedAt
                                                ? new Date(selectedParticipant.appliedAt).toLocaleString('ru-RU')
                                                : '-'}
                                        </td>
                                    </tr>
                                    {selectedParticipant.publicProfileId && (
                                        <tr>
                                            <td><strong>Профиль:</strong></td>
                                            <td>
                                                <a
                                                    href={`/profiles/${selectedParticipant.publicProfileId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Открыть профиль
                                                </a>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {selectedParticipant.notes && (
                                <>
                                    <hr />
                                    <div className="participant-notes">
                                        <h6>Примечания</h6>
                                        <p style={{ color: '#b0b6c3' }}>{selectedParticipant.notes}</p>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Закрыть
                    </Button>
                    {selectedParticipant?.applicationStatus === 'PENDING' && (
                        <>
                            <Button
                                variant="success"
                                onClick={() => {
                                    handleApprove(selectedParticipant);
                                    setShowDetailModal(false);
                                }}
                                disabled={actionLoading}
                            >
                                Одобрить
                            </Button>
                            <Button
                                variant="danger"
                                onClick={() => {
                                    handleReject(selectedParticipant);
                                    setShowDetailModal(false);
                                }}
                                disabled={actionLoading}
                            >
                                Отклонить
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminBattleParticipants;